import React, { useEffect, useRef, useState } from 'react';
import { MediaRecorderService } from './MediaRecorder.service';
import { Loader2Icon, MicIcon } from 'lucide-react';
import { AudioLevelMeter } from './AudioLevelMeter';
export function AudioRecorderComponent({ onFinalAudio, background, alignTooltip, onActive }) {
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isNotListening, setIsNotListening] = useState(false);
    const [volumeLevel, setVolumeLevel] = useState(0);
    const mediaRecorder = useRef(new MediaRecorderService()).current;
    let backgroundClass = background === 'white' ? 'bg-white' : 'bg-black';
    let textColor = background === 'white' ? 'text-gray-600' : 'text-white';
    const rawLevel = Math.max(0, Math.min(1, volumeLevel ?? 0));
    const EASE_EXP = 0.5; // <1 = ease-out (boost lows, compress highs).
    const eased = Math.pow(rawLevel, EASE_EXP);
    const minScale = 0.4;
    const maxScale = 1;
    const scale = minScale + (maxScale - minScale) * eased;
    useEffect(() => {
        mediaRecorder.setOnFinalAudio((audio) => {
            setIsProcessing(true);
            setIsRecording(false);
            // Create a wrapper function that will reset the processing state after
            // onFinalAudio completes
            const handleAudioProcessing = async (audioFile) => {
                try {
                    // @TODO: FLU-193 Handle this.
                    await onFinalAudio(audioFile);
                }
                finally {
                    setIsProcessing(false);
                    onActive?.(false);
                }
            };
            handleAudioProcessing(audio);
        });
        // live volume metering
        mediaRecorder.setOnLevel((lvl) => {
            setVolumeLevel(lvl);
        });
        // Issue detection
        mediaRecorder.setOnInputIssue((issue) => {
            console.log('Input issue detected:', issue);
            setIsNotListening(true);
        });
        mediaRecorder.setOnInputIssueResolved(() => {
            setIsNotListening(false);
        });
    }, []);
    const handleStartRecording = async () => {
        try {
            await mediaRecorder.start();
            setIsRecording(true);
            onActive?.(true);
        }
        catch (error) {
            console.error('Error accessing microphone:', error);
            // @TODO: FLU-193 Handle this. See if we need to use error component over alert.
            alert('Failed to access the microphone.');
        }
    };
    const handleStopRecording = () => {
        mediaRecorder.stop();
    };
    return (React.createElement("div", { className: "flex items-center" },
        React.createElement("button", { onClick: isRecording ? handleStopRecording : handleStartRecording, disabled: isProcessing, className: `relative w-10 h-10 rounded-full ${backgroundClass} border flex items-center justify-center transition-colors duration-200 group shadow-sm ${isRecording
                ? 'border-gray-200'
                : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'} ${isProcessing ? 'cursor-not-allowed' : 'cursor-pointer'}`, title: isRecording ? 'Stop recording' : isProcessing ? 'Processing...' : 'Voice input', "aria-label": isRecording ? 'Stop recording' : isProcessing ? 'Processing' : 'Start voice input' },
            React.createElement("div", { className: "relative flex items-center justify-center w-full h-full" }, isProcessing ? (React.createElement(Loader2Icon, { className: `w-5 h-5 ${textColor} animate-spin` })) : isRecording ? (React.createElement(React.Fragment, null,
                React.createElement("span", { className: "absolute inset-0 rounded-full bg-red-600 pointer-events-none transition-transform duration-150 ease-out", style: { transform: `scale(${scale})` } }),
                React.createElement("span", { className: "mx-auto z-10 w-2 h-2 bg-white" }))) : (React.createElement(MicIcon, { className: `w-5 h-5 ${textColor} group-hover:text-orange-500` })))),
        isRecording && (React.createElement("div", { className: "flex flex-col items-center ml-3 mr-3" },
            React.createElement(AudioLevelMeter, { level: volumeLevel, isRecording: isRecording, barCount: 15, barWidth: 3, barGap: 2, height: 20 }),
            React.createElement("span", { className: "text-[10px] text-gray-500 whitespace-nowrap" }, "Stop to send"))),
        isProcessing && (React.createElement("div", { className: "flex items-center gap-2 ml-3" },
            React.createElement("span", { className: "text-sm text-gray-600" }, "Analyzing...")))));
}
